import { useEffect, useRef } from 'react'
import { useNextStep } from 'nextstepjs'
import { useHomeOnboardingAppTourGate } from 'src/context/home-onboarding-app-tour-gate-context'
import { useSessionContext } from 'src/context/session-context'
import { localStorageService } from 'src/common/service/local-storage-service'
import { APP_TOUR_ID } from './steps'
import { useAppTourSteps } from './use-app-tour-steps'
import { useKeepAppTourCardInView } from './use-keep-app-tour-card-in-view'

/**
 * Waits for a DOM element matching `selector` to exist.
 * Needed for steps whose target is rendered async (e.g. after a data fetch).
 */
function waitForElement(selector: string, timeout = 5000): Promise<Element | null> {
  return new Promise((resolve) => {
    const existing = document.querySelector(selector)
    if (existing) {
      resolve(existing)
      return
    }

    const observer = new MutationObserver(() => {
      const found = document.querySelector(selector)
      if (found) {
        observer.disconnect()
        resolve(found)
      }
    })

    observer.observe(document.body, { childList: true, subtree: true })

    setTimeout(() => {
      observer.disconnect()
      resolve(null)
    }, timeout)
  })
}

export const AppTourController = (): null => {
  const { me } = useSessionContext()
  const { canStartHomeAppTour } = useHomeOnboardingAppTourGate()
  const { startNextStep, setCurrentStep } = useNextStep()
  const { tours, isResolving: isResolvingAppTourSteps } = useAppTourSteps()
  const started = useRef(false)

  useKeepAppTourCardInView()

  useEffect(() => {
    if (!me || me.appTourCompleted || started.current || !canStartHomeAppTour || isResolvingAppTourSteps) return
    started.current = true

    const savedStep = localStorageService.getAppTourStep()
    const targetStep = tours[0]?.steps[savedStep]

    if (targetStep?.selector) {
      // Wait for the target element to appear in the DOM (it may be rendered
      // async after a data fetch — e.g. step 0's "suggested-series" sits below
      // the featured-workouts shelf, which only mounts once the catalog-access
      // query resolves), then scroll it into view instantly so NextStep
      // calculates the correct overlay position before the tour starts.
      void waitForElement(targetStep.selector).then((el) => {
        el?.scrollIntoView({ behavior: 'auto', block: 'center' })
        startNextStep(APP_TOUR_ID)
        if (savedStep > 0) {
          setCurrentStep(savedStep, 100)
        }
      })
    } else {
      startNextStep(APP_TOUR_ID)
      if (savedStep > 0) {
        setCurrentStep(savedStep, 50)
      }
    }
  }, [me, me?.id, canStartHomeAppTour, isResolvingAppTourSteps, tours, setCurrentStep, startNextStep])

  return null
}
